import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/* Compat/compant linting is disabled due to only supporting modern browsers.
 * See the function canUseMediaCapture in /packages/media-capture/src/utils/index.js
 * for more information.
 */
/* eslint-disable compat/compat */

export function enumerateDevices() {
  return navigator.mediaDevices.enumerateDevices().then(devices => {
    const audioinput = devices.filter(({
      kind
    }) => kind === 'audioinput');
    const videoinput = devices.filter(({
      kind
    }) => kind === 'videoinput');
    return {
      audioinput,
      videoinput
    };
  });
}
function buildMediaConstraint(deviceId, config) {
  if (typeof deviceId !== 'string') return false;
  return deviceId.length > 0 ? _objectSpread(_objectSpread({}, config), {}, {
    deviceId: {
      exact: deviceId
    }
  }) : _objectSpread({}, config);
}
export function getWebcamStream({
  audioDeviceId,
  videoDeviceId,
  video: webcamVideoConfig = {},
  audio: webcamAudioConfig = {}
}) {
  if (typeof videoDeviceId !== 'string' && typeof audioDeviceId !== 'string') return Promise.resolve(null);
  return navigator.mediaDevices.getUserMedia({
    video: buildMediaConstraint(videoDeviceId, webcamVideoConfig),
    audio: buildMediaConstraint(audioDeviceId, webcamAudioConfig)
  });
}
export async function getDesktopStream(config) {
  try {
    const controller = window.CaptureController ? new window.CaptureController() : void 0;
    const stream = await navigator.mediaDevices.getDisplayMedia(_objectSpread({
      controller
    }, config));
    const _stream$getVideoTrack = stream.getVideoTracks(),
      _stream$getVideoTrack2 = _slicedToArray(_stream$getVideoTrack, 1),
      track = _stream$getVideoTrack2[0];
    const displaySurface = track.getSettings().displaySurface;

    // setFocusBehavior can only be called right after getDisplayMedia call
    // or you get an error about closed call window whatever it should mean.
    // Also setFocusBehavior can only be called for 'browser' (tab) or 'window'
    // surface values and not the whole screen aka 'monitor'
    if (controller && displaySurface !== 'monitor') {
      var _config$experimentalF;
      controller.setFocusBehavior((_config$experimentalF = config.experimentalFocusBehavior) !== null && _config$experimentalF !== void 0 ? _config$experimentalF : 'no-focus-change');
    }
    return stream;
  } catch (error) {
    if (error.name === 'NotAllowedError' && error.message.startsWith('Permission denied')) {
      return null;
    }
    throw error;
  }
}